import { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { useSEO } from '../../hooks/useSEO';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, FileText, Users, Send, Trash2, RefreshCw, Eye,
  Check, Clock, Sparkles, Shield, ShieldCheck, Mail, BarChart3, X
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => {
  const token = localStorage.getItem('dardzair_token');
  return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
};

export default function AdminPage() {
  const { language } = useLanguage();
  const { isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isFr = language === 'fr';
  const [tab, setTab] = useState('dashboard');

  useSEO({ title: 'Administration - Beytam' });

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) navigate('/login');
    else if (!isAdmin) navigate('/');
  }, [isAuthenticated, isAdmin, authLoading, navigate]);

  if (authLoading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin w-8 h-8 border-3 border-blue-900 border-t-transparent rounded-full" /></div>;
  if (!isAdmin) return null;

  const tabs = [
    { id: 'dashboard', label: isFr ? 'Tableau de bord' : 'لوحة التحكم', icon: BarChart3 },
    { id: 'blog', label: isFr ? 'Blog' : 'المدونة', icon: FileText },
    { id: 'users', label: isFr ? 'Utilisateurs' : 'المستخدمون', icon: Users },
  ];

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="admin-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-2 mb-6">
          <Shield className="w-7 h-7 text-blue-900" />
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800" data-testid="admin-title">
            {isFr ? 'Administration' : 'الإدارة'}
          </h1>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white rounded-xl p-1 border border-slate-100 shadow-sm w-fit" data-testid="admin-tabs">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${tab === t.id ? 'bg-blue-900 text-white shadow' : 'text-slate-600 hover:bg-slate-50'}`}
              data-testid={`tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === 'dashboard' && <DashboardTab isFr={isFr} />}
        {tab === 'blog' && <BlogTab isFr={isFr} />}
        {tab === 'users' && <UsersTab isFr={isFr} />}
      </div>
    </div>
  );
}

/* ── Dashboard Tab ── */
function DashboardTab({ isFr }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/admin/stats`, { headers: getHeaders() }).then(r => r.json()).then(setStats).catch(() => {});
  }, []);
  if (!stats) return <div className="text-center py-8 text-slate-400">Chargement...</div>;
  const cards = [
    { label: isFr ? 'Utilisateurs' : 'المستخدمون', value: stats.users, icon: Users, color: 'bg-blue-50 text-blue-700' },
    { label: isFr ? 'Annonces' : 'الإعلانات', value: stats.properties, icon: LayoutDashboard, color: 'bg-emerald-50 text-emerald-700' },
    { label: isFr ? 'Articles publiés' : 'مقالات منشورة', value: stats.articles_published, icon: FileText, color: 'bg-violet-50 text-violet-700' },
    { label: isFr ? 'Brouillons blog' : 'مسودات', value: stats.article_drafts, icon: Clock, color: 'bg-amber-50 text-amber-700' },
    { label: isFr ? 'Alertes actives' : 'تنبيهات نشطة', value: stats.active_alerts, icon: Mail, color: 'bg-rose-50 text-rose-700' },
    { label: isFr ? 'Demandes contact' : 'طلبات التواصل', value: stats.contacts, icon: Mail, color: 'bg-sky-50 text-sky-700' },
    { label: isFr ? 'Agences' : 'الوكالات', value: stats.agencies, icon: Shield, color: 'bg-indigo-50 text-indigo-700' },
    { label: isFr ? 'Emails envoyés/logués' : 'بريد مرسل/مسجل', value: `${stats.emails_sent}/${stats.emails_logged}`, icon: Send, color: 'bg-slate-100 text-slate-700' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="admin-stats">
      {cards.map(c => (
        <div key={c.label} className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm">
          <div className={`w-9 h-9 rounded-lg ${c.color} flex items-center justify-center mb-3`}><c.icon className="w-4.5 h-4.5" /></div>
          <p className="text-2xl font-bold text-slate-800">{c.value}</p>
          <p className="text-xs text-slate-500 mt-0.5">{c.label}</p>
        </div>
      ))}
    </div>
  );
}

/* ── Blog Tab ── */
function BlogTab({ isFr }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = () => {
    fetch(`${API}/api/blog-admin/drafts`, { headers: getHeaders() }).then(r => r.json()).then(d => { if (Array.isArray(d)) setDrafts(d); }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    toast.info(isFr ? 'Génération en cours (~30s)...' : 'جاري الإنشاء (~30 ثانية)...');
    try {
      const res = await fetch(`${API}/api/blog-admin/generate`, { method: 'POST', headers: getHeaders() });
      if (res.ok) { toast.success(isFr ? 'Article généré !' : 'تم إنشاء المقال!'); load(); }
      else { const d = await res.json(); toast.error(d.detail || 'Erreur'); }
    } catch { toast.error('Erreur réseau'); }
    setGenerating(false);
  };

  const publish = async (id) => {
    setPublishing(id);
    try {
      const res = await fetch(`${API}/api/blog-admin/publish/${id}`, { method: 'POST', headers: getHeaders() });
      if (res.ok) { toast.success(isFr ? 'Publié !' : 'تم النشر!'); load(); }
      else { const d = await res.json(); toast.error(d.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
    setPublishing(null);
  };

  const remove = async (id) => {
    if (!window.confirm(isFr ? 'Supprimer ce brouillon ?' : 'حذف هذه المسودة؟')) return;
    await fetch(`${API}/api/blog-admin/drafts/${id}`, { method: 'DELETE', headers: getHeaders() });
    toast.success(isFr ? 'Supprimé' : 'تم الحذف'); load();
  };

  const catLabel = (c) => ({ guide: 'Guide', diaspora: 'Diaspora', actualite: 'Actualité', quartier: 'Quartier' }[c] || c);

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-sm text-slate-500">{drafts.filter(d => d.status === 'draft').length} {isFr ? 'brouillons' : 'مسودات'} · {drafts.filter(d => d.status === 'published').length} {isFr ? 'publiés' : 'منشورة'}</p>
        </div>
        <button onClick={generate} disabled={generating}
          className="flex items-center gap-2 bg-blue-900 hover:bg-blue-800 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
          data-testid="generate-article-btn">
          {generating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {generating ? (isFr ? 'Génération...' : 'جاري الإنشاء...') : (isFr ? 'Générer un article' : 'إنشاء مقال')}
        </button>
      </div>

      {loading ? <p className="text-center py-8 text-slate-400">Chargement...</p> : drafts.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
          <Sparkles className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">{isFr ? 'Aucun article. Cliquez "Générer" pour commencer.' : 'لا توجد مقالات. انقر "إنشاء" للبدء.'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {drafts.map(d => (
            <div key={d.id} className={`bg-white rounded-xl border ${d.status === 'published' ? 'border-emerald-200' : 'border-slate-100'} shadow-sm p-4`} data-testid={`blog-draft-${d.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{catLabel(d.category)}</span>
                    <span className="text-[11px] text-slate-400">{d.city}</span>
                    {d.target_audience === 'diaspora' && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">Diaspora</span>}
                    {d.status === 'published' ? <span className="flex items-center gap-1 text-[11px] text-emerald-600 font-medium"><Check className="w-3 h-3" />{isFr ? 'Publié' : 'منشور'}</span>
                      : <span className="flex items-center gap-1 text-[11px] text-amber-600 font-medium"><Clock className="w-3 h-3" />{isFr ? 'Brouillon' : 'مسودة'}</span>}
                  </div>
                  <h3 className="font-bold text-slate-800 truncate">{d.title}</h3>
                  <p className="text-sm text-slate-500 mt-0.5 line-clamp-1">{d.excerpt}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => setPreview(preview === d.id ? null : d.id)} className="p-2 text-slate-400 hover:text-blue-900 hover:bg-slate-50 rounded-lg" title={isFr ? 'Aperçu' : 'معاينة'}>
                    <Eye className="w-4 h-4" />
                  </button>
                  {d.status === 'draft' && (
                    <>
                      <button onClick={() => publish(d.id)} disabled={publishing === d.id}
                        className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
                        data-testid={`publish-btn-${d.id}`}>
                        {publishing === d.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        {isFr ? 'Publier' : 'نشر'}
                      </button>
                      <button onClick={() => remove(d.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg" title={isFr ? 'Supprimer' : 'حذف'}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              {preview === d.id && (
                <div className="border-t border-slate-100 mt-3 pt-3">
                  <div className="prose prose-sm max-w-none text-slate-700">
                    {d.content?.includes('<') ? <div dangerouslySetInnerHTML={{ __html: d.content }} /> : <p className="whitespace-pre-line">{d.content}</p>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Users Tab ── */
function UsersTab({ isFr }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    fetch(`${API}/api/admin/users`, { headers: getHeaders() }).then(r => r.json()).then(d => { if (Array.isArray(d)) setUsers(d); }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toggleRole = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    if (!window.confirm(isFr ? `Changer le rôle en "${newRole}" ?` : `تغيير الدور إلى "${newRole}"؟`)) return;
    await fetch(`${API}/api/admin/users/${userId}/role`, { method: 'PUT', headers: getHeaders(), body: JSON.stringify({ role: newRole }) });
    toast.success(isFr ? 'Rôle modifié' : 'تم تعديل الدور'); load();
  };

  const removeUser = async (userId, email) => {
    if (!window.confirm(isFr ? `Supprimer ${email} ?` : `حذف ${email}؟`)) return;
    const res = await fetch(`${API}/api/admin/users/${userId}`, { method: 'DELETE', headers: getHeaders() });
    if (res.ok) { toast.success(isFr ? 'Utilisateur supprimé' : 'تم حذف المستخدم'); load(); }
    else { const d = await res.json(); toast.error(d.detail || 'Erreur'); }
  };

  if (loading) return <p className="text-center py-8 text-slate-400">Chargement...</p>;

  return (
    <div data-testid="admin-users">
      <p className="text-sm text-slate-500 mb-4">{users.length} {isFr ? 'utilisateurs inscrits' : 'مستخدم مسجل'}</p>
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="users-table">
            <thead>
              <tr className="bg-slate-50 text-xs text-slate-500 uppercase">
                <th className="text-left p-3 font-medium">{isFr ? 'Nom' : 'الاسم'}</th>
                <th className="text-left p-3 font-medium">Email</th>
                <th className="text-center p-3 font-medium">{isFr ? 'Type' : 'النوع'}</th>
                <th className="text-center p-3 font-medium">{isFr ? 'Rôle' : 'الدور'}</th>
                <th className="text-center p-3 font-medium">{isFr ? 'Inscrit le' : 'تاريخ التسجيل'}</th>
                <th className="text-center p-3 font-medium">{isFr ? 'Actions' : 'الإجراءات'}</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-t border-slate-50 hover:bg-slate-50/50">
                  <td className="p-3 text-sm font-medium text-slate-800">{u.full_name}</td>
                  <td className="p-3 text-sm text-slate-600">{u.email}</td>
                  <td className="p-3 text-center">
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{u.user_type}</span>
                  </td>
                  <td className="p-3 text-center">
                    {u.role === 'admin' ? (
                      <span className="flex items-center justify-center gap-1 text-xs font-medium text-blue-700"><ShieldCheck className="w-3.5 h-3.5" />Admin</span>
                    ) : (
                      <span className="text-xs text-slate-400">User</span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-slate-400 text-center">{u.created_at ? new Date(u.created_at).toLocaleDateString('fr-FR') : '-'}</td>
                  <td className="p-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => toggleRole(u.id, u.role || 'user')}
                        className="p-1.5 text-slate-400 hover:text-blue-700 hover:bg-blue-50 rounded-lg" title={isFr ? 'Changer rôle' : 'تغيير الدور'}
                        data-testid={`role-toggle-${u.id}`}>
                        <Shield className="w-4 h-4" />
                      </button>
                      <button onClick={() => removeUser(u.id, u.email)}
                        className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg" title={isFr ? 'Supprimer' : 'حذف'}
                        data-testid={`delete-user-${u.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
